import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { forceCollide, forceManyBody, SimulationNodeDatum } from 'd3-force';
import {
  flattenGraph,
  depthFromZoom,
  FlatNode,
  findNode,
  GRAPH_ROOT,
} from '../../data/graph';
import type { ViewName } from '../../types';

interface KnowledgeGraphProps {
  onGo: (name: ViewName) => void;
  onOpenProject: (id: string) => void;
}

type SimNode = FlatNode & {
  x?: number;
  y?: number;
  vx?: number;
  vy?: number;
};

const COLORS: Record<FlatNode['kind'], { fill: string; stroke: string; glow: string }> = {
  root: { fill: '#A8E831', stroke: '#C8F56A', glow: 'rgba(168,232,49,0.35)' },
  branch: { fill: '#1a2e14', stroke: '#A8E831', glow: 'rgba(168,232,49,0.2)' },
  item: { fill: '#0F140F', stroke: '#5A6B54', glow: 'rgba(90,107,84,0.25)' },
  metric: { fill: '#0c1816', stroke: '#5EEAD4', glow: 'rgba(94,234,212,0.25)' },
};

const RADIUS: Record<FlatNode['kind'], number> = {
  root: 20,
  branch: 10,
  item: 7,
  metric: 5.5,
};

const FOCUS_ZOOM: Record<FlatNode['kind'], number> = {
  root: 1.05,
  branch: 1.55,
  item: 2.15,
  metric: 2.7,
};

function asNode(n: string | SimNode | object | undefined): SimNode | null {
  if (!n || typeof n === 'string') return null;
  if (typeof n === 'object' && 'depth' in n && 'kind' in n) return n as SimNode;
  return null;
}

const KnowledgeGraph: React.FC<KnowledgeGraphProps> = ({ onGo, onOpenProject }) => {
  const wrapRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const fgRef = useRef<any>(null);
  const [size, setSize] = useState({ w: 640, h: 520 });
  const [zoomK, setZoomK] = useState(1);
  const [selectedId, setSelectedId] = useState<string | null>('me');
  const [hoverId, setHoverId] = useState<string | null>(null);

  const base = useMemo(() => flattenGraph(), []);
  const maxDepth = depthFromZoom(zoomK);

  const graphData = useMemo(
    () => ({
      nodes: base.nodes.map((n) => ({ ...n })),
      links: base.links.map((l) => ({ ...l })),
    }),
    [base]
  );

  const selectedTree = selectedId ? findNode(GRAPH_ROOT, selectedId) : null;

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const measure = () => {
      const r = el.getBoundingClientRect();
      setSize({ w: Math.floor(r.width), h: Math.floor(r.height) });
    };
    measure();
    let raf = 0;
    const ro = new ResizeObserver(() => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(measure);
    });
    ro.observe(el);

    // Keep Ctrl/Cmd+wheel and trackpad pinch from zooming the browser page
    const stopPageZoom = (e: WheelEvent) => {
      e.preventDefault();
    };
    const stopGesture = (e: Event) => {
      e.preventDefault();
    };
    el.addEventListener('wheel', stopPageZoom, { passive: false, capture: true });
    el.addEventListener('gesturestart', stopGesture, { passive: false, capture: true } as AddEventListenerOptions);
    el.addEventListener('gesturechange', stopGesture, { passive: false, capture: true } as AddEventListenerOptions);
    el.addEventListener('gestureend', stopGesture, { passive: false, capture: true } as AddEventListenerOptions);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      el.removeEventListener('wheel', stopPageZoom, true);
      el.removeEventListener('gesturestart', stopGesture, true);
      el.removeEventListener('gesturechange', stopGesture, true);
      el.removeEventListener('gestureend', stopGesture, true);
    };
  }, []);

  useEffect(() => {
    const fg = fgRef.current;
    if (!fg) return;
    try {
      // Stronger repulsion + collide so clusters don't tangle
      fg.d3Force(
        'charge',
        forceManyBody()
          .strength((node: SimulationNodeDatum) => {
            const d = (node as SimNode).depth ?? 1;
            if (d === 0) return -520;
            if (d === 1) return -420;
            if (d === 2) return -280;
            return -160;
          })
          .distanceMax(520)
      );
      const link = fg.d3Force('link');
      if (link) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        link.distance((l: any) => {
          const t = asNode(l.target);
          const depth = t?.depth ?? l.depth ?? 2;
          if (depth <= 1) return 160;
          if (depth === 2) return 120;
          return 88;
        });
        link.strength(0.35);
      }
      fg.d3Force(
        'collide',
        forceCollide((node: SimulationNodeDatum) => {
          const n = node as SimNode;
          const r = RADIUS[n.kind] || 6;
          return r + (n.kind === 'metric' ? 28 : n.kind === 'item' ? 34 : 42);
        }).iterations(3)
      );
      fg.d3ReheatSimulation?.();
    } catch {
      /* force helpers differ by version */
    }
    const t = window.setTimeout(() => fg.zoomToFit?.(600, 64), 800);
    return () => clearTimeout(t);
  }, [size.w, size.h]);

  const isVisible = useCallback((depth: number) => depth <= maxDepth, [maxDepth]);

  const paintNode = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (node: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const n = node as SimNode;
      if (!isVisible(n.depth)) return;
      const r = RADIUS[n.kind] || 6;
      const c = COLORS[n.kind];
      const x = n.x || 0;
      const y = n.y || 0;
      const isSel = n.id === selectedId;
      const isHover = n.id === hoverId;

      if (n.kind === 'root' || isSel || isHover) {
        ctx.beginPath();
        ctx.arc(x, y, r + 5, 0, Math.PI * 2);
        ctx.fillStyle = c.glow;
        ctx.fill();
      }

      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      ctx.fillStyle = isSel && n.kind !== 'root' ? '#152015' : c.fill;
      ctx.fill();
      ctx.lineWidth = (isSel || isHover ? 2.2 : 1.4) / globalScale;
      ctx.strokeStyle = isSel ? '#A8E831' : c.stroke;
      ctx.stroke();

      const showLabel = n.depth <= 1 || globalScale > 1.05 || isSel || isHover;
      if (!showLabel && n.depth > 1) return;

      const fontSize =
        Math.max(
          (n.kind === 'root' ? 15 : n.kind === 'branch' ? 13 : n.kind === 'item' ? 12 : 11) / globalScale,
          n.kind === 'root' ? 9 : n.kind === 'branch' ? 7 : 5.5
        );
      ctx.font = `${n.kind === 'root' || n.kind === 'branch' ? 600 : 500} ${fontSize}px JetBrains Mono, monospace`;
      ctx.textAlign = 'center';

      if (n.kind === 'root') {
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#080A08';
        ctx.fillText(n.label, x, y);
        return;
      }

      ctx.textBaseline = 'top';
      const label =
        n.label.length > 26 && globalScale < 1.2 ? `${n.label.slice(0, 24)}…` : n.label;
      ctx.fillStyle = isSel ? '#A8E831' : '#D5DED0';
      ctx.fillText(label, x, y + r + 3 / globalScale);

      if ((isSel || isHover || globalScale > 1.35) && n.sub) {
        ctx.fillStyle = '#6B7D66';
        ctx.font = `400 ${Math.max(10 / globalScale, 4.5)}px JetBrains Mono, monospace`;
        const sub = n.sub.length > 32 ? `${n.sub.slice(0, 30)}…` : n.sub;
        ctx.fillText(sub, x, y + r + 16 / globalScale);
      }
    },
    [selectedId, hoverId, isVisible]
  );

  const paintLink = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (link: any, ctx: CanvasRenderingContext2D, globalScale: number) => {
      const s = asNode(link.source);
      const t = asNode(link.target);
      if (!s || !t || s.x == null || t.x == null || s.y == null || t.y == null) return;
      if (!isVisible(s.depth) || !isVisible(t.depth)) return;

      const highlight =
        s.id === selectedId || t.id === selectedId || s.id === hoverId || t.id === hoverId;
      ctx.beginPath();
      ctx.moveTo(s.x, s.y);
      ctx.lineTo(t.x, t.y);
      ctx.strokeStyle = highlight ? 'rgba(168,232,49,0.55)' : 'rgba(30,39,30,0.95)';
      ctx.lineWidth = (highlight ? 1.6 : 1) / globalScale;
      ctx.stroke();
    },
    [selectedId, hoverId, isVisible]
  );

  const onNodeClick = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (node: any) => {
      const n = node as SimNode;
      setSelectedId(n.id);
      const fg = fgRef.current;
      if (!fg || n.x == null || n.y == null) return;
      const targetZoom = Math.min(4, Math.max(zoomK, FOCUS_ZOOM[n.kind] ?? 1.8));
      fg.centerAt(n.x, n.y, 650);
      fg.zoom(targetZoom, 650);
      setZoomK(targetZoom);
    },
    [zoomK]
  );

  const resetView = () => {
    setSelectedId('me');
    setZoomK(0.55);
    fgRef.current?.zoom?.(0.55, 400);
    window.setTimeout(() => fgRef.current?.zoomToFit?.(400, 56), 420);
  };

  const lodLabel = maxDepth <= 1 ? 'overview' : maxDepth === 2 ? 'entities' : 'metrics';

  return (
    <div className="kg-wrap" ref={wrapRef}>
      {size.w > 0 && size.h > 0 && (
        <ForceGraph2D
          ref={fgRef}
          width={size.w}
          height={size.h}
          graphData={graphData}
          backgroundColor="#080A08"
          nodeId="id"
          linkSource="source"
          linkTarget="target"
          nodeRelSize={6}
          linkDirectionalParticles={maxDepth >= 2 ? 1 : 0}
          linkDirectionalParticleWidth={1.2}
          linkDirectionalParticleSpeed={0.004}
          linkDirectionalParticleColor={() => 'rgba(168,232,49,0.45)'}
          cooldownTicks={140}
          d3AlphaDecay={0.028}
          d3VelocityDecay={0.28}
          enableNodeDrag
          enableZoomInteraction
          enablePanInteraction
          minZoom={0.25}
          maxZoom={4}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          nodeVisibility={(n: any) => isVisible((n as SimNode).depth)}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          linkVisibility={(l: any) => {
            const s = asNode(l.source);
            const t = asNode(l.target);
            if (s && t) return isVisible(s.depth) && isVisible(t.depth);
            return isVisible(l.depth ?? 99);
          }}
          onZoom={({ k }) => setZoomK(k)}
          onNodeClick={onNodeClick}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onNodeHover={(n: any) => setHoverId(n ? (n as SimNode).id : null)}
          onBackgroundClick={() => setSelectedId('me')}
          nodeCanvasObject={paintNode}
          nodeCanvasObjectMode={() => 'replace'}
          linkCanvasObject={paintLink}
          linkCanvasObjectMode={() => 'replace'}
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          nodePointerAreaPaint={(node: any, color: string, ctx: CanvasRenderingContext2D) => {
            const n = node as SimNode;
            if (!isVisible(n.depth)) return;
            const r = (RADIUS[n.kind] || 6) + 4;
            ctx.beginPath();
            ctx.arc(n.x || 0, n.y || 0, r, 0, Math.PI * 2);
            ctx.fillStyle = color;
            ctx.fill();
          }}
        />
      )}

      <div className="kg-hud">
        <div className="kg-hint">
          click to focus · scroll zoom · lod: <span className="lime">{lodLabel}</span>
        </div>
        <div className="kg-tools">
          <button
            type="button"
            aria-label="Zoom in"
            onClick={() => {
              const next = Math.min(4, zoomK * 1.35);
              fgRef.current?.zoom?.(next, 300);
              setZoomK(next);
            }}
          >
            +
          </button>
          <button
            type="button"
            aria-label="Zoom out"
            onClick={() => {
              const next = Math.max(0.25, zoomK * 0.74);
              fgRef.current?.zoom?.(next, 300);
              setZoomK(next);
            }}
          >
            −
          </button>
          <button type="button" onClick={resetView}>
            reset
          </button>
        </div>
      </div>

      {selectedTree && selectedTree.id !== 'me' && (() => {
        const action = selectedTree.action;
        return (
          <div className="kg-detail">
            <span className="label">{selectedTree.kind}</span>
            <strong>{selectedTree.label}</strong>
            {selectedTree.sub && <p>{selectedTree.sub}</p>}
            {action?.type === 'link' && (
              <a
                className="kg-open"
                href={action.href}
                target={action.href.startsWith('mailto:') ? undefined : '_blank'}
                rel="noopener noreferrer"
              >
                open {selectedTree.label} →
              </a>
            )}
            {action?.type === 'project' && (
              <button
                type="button"
                className="kg-open"
                onClick={() => onOpenProject(action.id)}
              >
                open {selectedTree.label} →
              </button>
            )}
            {action?.type === 'view' && (
              <button
                type="button"
                className="kg-open"
                onClick={() => onGo(action.view)}
              >
                go to {action.view} →
              </button>
            )}
            {maxDepth < 3 && <p className="kg-lod-tip">zoom in to reveal deeper nodes</p>}
          </div>
        );
      })()}
    </div>
  );
};

export default KnowledgeGraph;
